import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Swiper as SwiperInstance } from 'swiper';

export type Resource = Readonly<{
  alt: string;
  content: Readonly<{ guid: string; name: string; url: string }>;
  /** Idioma del `alt` cuando es contenido del curso (WCAG 3.1.2); sin él, hereda el de la página. */
  lang?: string;
}>;

type UseDialogCarrouselImagesParams = Readonly<{
  ariaLabel?: string;
  currentResource: Resource;
  isOpen: boolean;
  onSlideChange?: (swiper: SwiperInstance) => void;
  resources: ReadonlyArray<Resource>;
}>;

type UseDialogCarrouselImagesResult = Readonly<{
  activeIndex: number;
  closeLabel: string;
  handleSlideChange: (swiper: SwiperInstance) => void;
  initialIndex: number;
  resolvedAriaLabel: string;
  setThumbsSwiper: (swiper: SwiperInstance | null) => void;
  thumbsSwiper: SwiperInstance | null;
}>;

function resolveInitialIndex(
  resources: ReadonlyArray<Resource>,
  currentResource: Resource,
): number {
  const index = resources.findIndex(
    (resource) => resource.content.guid === currentResource.content.guid,
  );
  return Math.max(index, 0);
}

function useDialogCarrouselImages({
  ariaLabel,
  currentResource,
  isOpen,
  onSlideChange,
  resources,
}: UseDialogCarrouselImagesParams): UseDialogCarrouselImagesResult {
  const { t } = useTranslation(['actions', 'common']);
  const initialIndex = resolveInitialIndex(resources, currentResource);
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance | null>(null);

  // MUI Dialog unmounts children on close, which destroys the inner Swipers.
  // We must release the (now destroyed) thumbs reference so the next open
  // initializes against a fresh instance instead of throwing inside Swiper.
  useEffect(() => {
    if (!isOpen) setThumbsSwiper(null);
  }, [isOpen]);

  function handleSlideChange(swiper: SwiperInstance): void {
    setActiveIndex(swiper.activeIndex);
    if (onSlideChange) onSlideChange(swiper);
  }

  const closeLabel = t('actions:Close');
  const resolvedAriaLabel = ariaLabel ?? t('common:Image gallery');

  return {
    activeIndex,
    closeLabel,
    handleSlideChange,
    initialIndex,
    resolvedAriaLabel,
    setThumbsSwiper,
    thumbsSwiper,
  };
}

export default useDialogCarrouselImages;
